/** @jsxImportSource @emotion/react */
import { forwardRef } from "react";
import { useControlledState } from "./hooks/useControlledState";
import { useStyles } from "./hooks/useStyles";
import { SwitchProps } from "./types";

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      id,
      checked,
      defaultChecked,
      disabled,
      labelChildren,
      onClick,
      onChange,
      onKeyDown,
      loading,
      checkedChildren = "On",
      unCheckedChildren = "Off",
      a11yLoadingMessage = "Loading... Please wait",
      ...restProps
    },
    ref,
  ) => {
    const [innerChecked, setInnerChecked] = useControlledState<boolean>(false, {
      value: checked,
      defaultValue: defaultChecked,
    });
    const allowUpdate = !disabled || !loading;
    const styles = useStyles();

    function triggerChange(
      newChecked: boolean,
      event:
        | React.MouseEvent<HTMLButtonElement>
        | React.KeyboardEvent<HTMLButtonElement>,
    ) {
      let mergedChecked = innerChecked;
      if (allowUpdate) {
        mergedChecked = newChecked;
        setInnerChecked(mergedChecked);
        onChange?.(mergedChecked, event);
      }
      return mergedChecked;
    }

    // Keyboard navigation to toggle switch
    function onInternalKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
      if (allowUpdate) {
        const keyMap = {
          ArrowLeft: () => triggerChange(false, e),
          ArrowRight: () => triggerChange(true, e),
        };
        // Allow only keys in the Map to trigger a toggle.
        Object.keys(keyMap).includes(e.key) && keyMap[e.key]();
        onKeyDown?.(e);
      }
    }

    // Fallback trigger onClick returns value(as a button)
    function onInternalClick(e: React.MouseEvent<HTMLButtonElement>) {
      if (allowUpdate) {
        const returnValue = triggerChange(!innerChecked, e);
        onClick?.(returnValue, e);
      }
    }

    return (
      <div css={styles.root}>
        {labelChildren && <label htmlFor={id}>{labelChildren}</label>}
        <div css={{ position: "relative" }}>
          <button
            {...restProps}
            id={id}
            type="button"
            role="switch"
            aria-checked={innerChecked}
            disabled={disabled || loading}
            ref={ref}
            onKeyDown={onInternalKeyDown}
            onClick={onInternalClick}
            css={styles.button}
          >
            <span css={styles.text}>
              {innerChecked ? checkedChildren : unCheckedChildren}
            </span>
          </button>
          <div css={styles.handle}>
            {loading && (
              <div
                role="alert"
                css={styles.loading}
                data-testid="switch-loading-indicator"
              >
                {a11yLoadingMessage}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);
